import { HTMLAttributes, useState, useEffect, useRef, useCallback } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../ui/form";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../ui/button";
import { Camera, Edit, UploadIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import Technique from "@/assets/technique.png";
import { useForm } from "react-hook-form";
import { Input } from "../ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { installFlowSensorKitSerial } from "@/installFlow/storage-keys";


const formSchema = z.object({
  serialNumber: z.string().min(1, "Serial Number is required"),
});

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export enum ScanStatus {
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}
export interface NameplateScanCreateResponse {
  id: string;
  status: ScanStatus;
}

/** Valid identify response shape – only then we allow persisting serial and proceeding */
export interface ValidIdentifyResponse {
  identity: {
    manufacturer: string;
    model_number: string;
    serial_number: string;
  };
  confidence: number;
  source: string;
  qr_found: boolean;
}

function isValidScanResponse(
  data: unknown,
): data is ValidIdentifyResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  if (d.confidence !== 1.0 || d.source !== "qr" || d.qr_found !== true)
    return false;
  const identity = d.identity;
  if (!identity || typeof identity !== "object") return false;
  const id = identity as Record<string, unknown>;
  return (
    typeof id.manufacturer === "string" &&
    typeof id.model_number === "string" &&
    typeof id.serial_number === "string"
  );
}

export function SensorScan({ className }: AuthProps) {
  const navigate = useNavigate();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serialNumber: "",
    },
  });

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  async function openCamera() {
    setCameraError(null);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      await new Promise<void>((resolve, reject) => {
        if (!videoRef.current) {
          reject(new Error("Video element not ready"));
          return;
        }
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().then(resolve).catch(reject);
        };
        videoRef.current.onerror = () =>
          reject(new Error("Video failed to load"));
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not access camera";
      setCameraError(
        message.includes("Permission") || message.includes("NotAllowed")
          ? "Camera access was denied. Please allow camera in your browser settings."
          : "Could not access camera. Please check permissions and try again.",
      );
      setCameraOpen(false);
    }
  }

  function handleCloseCamera(open: boolean) {
    if (!open) {
      stopStream();
      setCameraError(null);
      setCameraOpen(false);
    }
  }

  
  function dataUrlToFile(
    dataUrl: string,
    filename = "nameplate-photo.jpg",
  ): File {
    const [header, base64] = dataUrl.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
  }

 

  function goToReviewNamePlatePhoto() {
    fileInputRef.current?.click();
  }

  

  async function onSubmit(values: z.infer<typeof formSchema>) {
    installFlowSensorKitSerial.set(values.serialNumber);
    await navigate({
      to: "/installflow/auth-install-flow",
    } as unknown as Parameters<typeof navigate>[0]);
  }

  return (
    <>
     
      <div className="w-full max-w-[460px] h-full flex flex-col gap-6">
        <img src={Technique} alt="Technique" />
        <div className="text-left px-6 gap-3 flex flex-col">
          <h2 className="text-xl font-bold text-black leading-[26px]">
            Scan the QR code on your Thalo sensor kit
          </h2>
        </div>
        {/* <div className="w-full flex flex-col gap-2 px-6">
          <Button
            className="w-full h-[90px] p-4 flex items-center justify-center bg-[#0053EE] text-white"
            onClick={openCamera}
            disabled={isScanning}
          >
            <Camera className="w-5 h-5 mr-4" />
            Scan QR Code
          </Button>
          <Button
            className="w-full h-[90px] p-4 flex flex-col items-center justify-center border-2 border-[rgba(0, 0, 0, 0.1)] bg-white text-black"
            onClick={goToReviewNamePlatePhoto}
            disabled={isScanning}
          >
            <UploadIcon className="w-5 h-5 mr-4" />
            Upload photo
          </Button>
          {scanError && (
            <p className="text-sm text-destructive mt-1">{scanError}</p>
          )}
        </div> */}
        <div className="relative px-6">
          {/* <div className="absolute inset-0 flex items-center px-6">
            <span className="w-full border-t"></span>
          </div> */}
          {/* <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or enter manually
            </span>
          </div> */}
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-6">
            <FormField
              control={form.control}
              name="serialNumber"
              render={({ field }) => (
                <FormItem className="gap-4 flex flex-col">
                  <FormLabel>Serial Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Serial Number"
                      className="h-12 !mt-0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              variant="installFlow"
              type="submit"
              className="h-[54px] w-full"
              disabled={!form.formState.isValid}
            >
              Next
            </Button>
          </form>
        </Form>
      </div>

      <Dialog open={cameraOpen} onOpenChange={handleCloseCamera}>
        <DialogContent
          className="sm:max-w-[500px]"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Take nameplate photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="relative w-full aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            {cameraError && (
              <p className="text-sm text-destructive">{cameraError}</p>
            )}
          </div>
         
        </DialogContent>
      </Dialog>
    </>
  );
}
